'use client';

import * as Sentry from '@sentry/nextjs';
import { isBrowser } from '@/util';

export function SentryScript({ dsn, environment }: { dsn: string; environment: string }): null {
  if (!isBrowser) return null;

  if (!window.Sentry) {
    let sampleRate = 0;
    if (environment === 'production') {
      sampleRate = 0.1;
    } else if (environment === 'staging') {
      sampleRate = 0.01;
    } else if (environment === 'development') {
      /**
       * @note set to 1 when debugging
       */
      sampleRate = 0;
    }

    Sentry.init({
      dsn,
      tracesSampler: ({ name }) => {
        if (!name) return 0;
        return sampleRate;
      },
      environment,
      debug: process.env.NODE_ENV !== 'production',
      integrations: [
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
          maskAllInputs: true,
        }),
        Sentry.browserTracingIntegration({
          enableInp: true,
        }),
      ],
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: environment === 'production' ? 1 : 0,
      denyUrls: [
        // Block transactions and errors on widget pages
        /^(.*?(\*?.fixter.co.uk\/widgets)[^$]*)$/,
        /^(.*?(\*?.fixter.fr\/widgets)[^$]*)$/,
        // Block Microsoft Bat script errors
        'https://bat.bing.com/bat.js',
      ],
      ignoreErrors: [
        'cookieconsent',
        'widget',
        'GraphQL error: getSession',
        '_submitInjector',
        'HTMLScriptElement.t.onload',
        'chunks/pages/_app',
        // Ignore all widget-related errors
        /widgets/,
      ],
      beforeSend(event) {
        // Filter out hydration errors, especially those that might be caused by A/B testing
        if (
          event.exception?.values?.some((exception) => {
            const errorMsg = exception.value || '';
            return (
              // Common hydration errors
              errorMsg.includes('Hydration failed because') ||
              errorMsg.includes('Text content does not match server-rendered HTML') ||
              // Specific React hydration warnings that often occur with A/B testing
              errorMsg.includes('Expected server HTML to contain a matching') ||
              errorMsg.includes('did not match server-rendered HTML') ||
              errorMsg.includes('There was an error while hydrating') ||
              // Minified React error #425 (hydration error)
              errorMsg.includes('Minified React error #425') ||
              errorMsg.includes('Minified React error #423') ||
              errorMsg.includes('Minified React error #418')
            );
          })
        ) {
          return null;
        }
        return event;
      },
    });

    // Expose Sentry to window for debugging
    window.Sentry = Sentry;
  }

  return null;
}
